import { ErrorBoundary } from '@sentry/react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { useEntityByIdSuspenseQuery } from '@eluve/apollo-client';
import { BackBreadcrumbItem } from '@eluve/blocks';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Content,
  Header,
  SidebarTrigger,
  Stack,
} from '@eluve/components';
import { ChatWithPatientData } from '@eluve/feature-chat-with-patient-data';
import { getPatientDetailsQuery } from '@eluve/frontend-appointment-hooks';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';

import { PatientDetailsPage } from './PatientDetailsPage';

export const PatientDetailsPageLayout: React.FC<{ baseUrl: string }> = ({
  baseUrl,
}) => {
  const { tenantId, patientId } = useParams() as {
    tenantId: string;
    patientId: string;
  };
  const data = useEntityByIdSuspenseQuery(
    getPatientDetailsQuery,
    { tenantId, patientId },
    { entityKey: 'patientsByPk', entityType: 'Patient' },
  );
  const basicDetails = data.patientsByPk;

  return (
    <>
      <Header>
        <SidebarTrigger />
        <Breadcrumb className="flex-1">
          <BreadcrumbList>
            <BackBreadcrumbItem />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/organizations/${tenantId}/patients`}>Patients</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage className="privacy-text">
                {formatHumanName(basicDetails.firstName, basicDetails.lastName)}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <Stack justify="end" fullWidth={false} gap={2} direction="horizontal">
          <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
            <ChatWithPatientData
              tenantId={tenantId}
              patientId={patientId}
              baseUrl={baseUrl}
            />
          </TenantOrUserFeatureFlaggedComponent>
        </Stack>
      </Header>
      <Content padded>
        <ErrorBoundary fallback={<div>Error</div>}>
          <PatientDetailsPage />
        </ErrorBoundary>
      </Content>
    </>
  );
};
